import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import { print } from "graphql";
import BasicInfoStep from "./BasicInfoStep";
import ProviderStep from "./ProviderStep"; // Import the new component
import InstructorStep from "./InstructorStep";
import ScheduleStep from "./ScheduleStep";
import AdditionalDetailsStep from "./AdditionalDetailsStep";
import { TrainingInput } from "./types";
import { env } from "@/utils/env";
import { useToast } from "@/components/ui/use-toast"; // Make sure this import is added
import { getCookie } from "@/utils/cookies";

// GraphQL mutation
const CREATE_TRAINING = gql`
  mutation CreateTraining($input: CreateTrainingInput!, $image: Upload!) {
    createTraining(input: $input, image: $image) {
      id
      title
      image
      provider
      price
      duration
      startDate
      instructor {
        name
        email
      }
    }
  }
`;

interface AddTrainingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddTrainingModal: React.FC<AddTrainingModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [newTraining, setNewTraining] = useState<Partial<TrainingInput>>({
    title: "",
    provider: "",
    price: 0,
    duration: "",
    description: "",
    level: "Beginner",
    format: "Online",
    category: "Programming",
    startDate: "",
    language: "English",
    certificate: false,
    instructor: { name: "", email: "", specialization: "" },
    requirements: [],
    modules: [],
    labs: [],
    resources: [],
    location: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(""); // Added for image preview persistence
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const { toast } = useToast(); // Add this line to get the toast function
  const [createTraining] = useMutation(CREATE_TRAINING);

  const handleNext = () => {
    if (isStepComplete(currentStep)) setCurrentStep((prev) => prev + 1);
  };

  const handlePrevious = () => setCurrentStep((prev) => prev - 1);

  // Update the isStepComplete function to handle the new step order
  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(
          newTraining.title &&
          newTraining.price &&
          newTraining.duration &&
          newTraining.description &&
          newTraining.language &&
          imageFile &&
          (newTraining.format === "Online" || newTraining.location)
        );
      case 2:
        return !!newTraining.provider; // New provider step
      case 3:
        return !!(
          newTraining.instructor?.name &&
          newTraining.instructor?.email &&
          newTraining.instructor?.specialization
        );
      case 4:
        return !!newTraining.startDate;
      case 5:
        return !!newTraining.modules?.length;
      default:
        return false;
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setNewTraining({
      title: "",
      provider: "",
      price: 0,
      duration: "",
      description: "",
      level: "Beginner",
      format: "Online",
      category: "Programming",
      startDate: "",
      language: "English",
      certificate: false,
      instructor: { name: "", email: "", specialization: "" },
      requirements: [],
      modules: [],
      labs: [],
      resources: [],
      location: "",
    });
    setImageFile(null);
    setImagePreview(""); // Clear image preview on reset
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isStepComplete(currentStep)) return;
  
    try {
      setLoading(true);
      setError("");
  
      // Format the date properly or use a default date if empty
      const formattedStartDate = newTraining.startDate 
        ? new Date(newTraining.startDate).toISOString() 
        : new Date().toISOString();
  
      // Log the training data before submission for debugging
      console.log("Submitting training data:", {
        ...newTraining,
        providerId: newTraining.providerId
      });
      
      const operations = {
        query: print(CREATE_TRAINING),
        variables: {
          input: {
            ...newTraining,
            price: Number(newTraining.price),
            startDate: formattedStartDate,
            certificate: Boolean(newTraining.certificate),
            // Ensure provider name is set
            provider: newTraining.provider,
            // Ensure providerId is properly passed if it exists
            providerId: newTraining.providerId ? Number(newTraining.providerId) : undefined,
            instructor: {
              name: newTraining.instructor?.name || "",
              email: newTraining.instructor?.email || "",
              specialization: newTraining.instructor?.specialization || "",
            },
            location: newTraining.format !== "Online" ? newTraining.location : null,
          },
          image: null,
        },
      };

      const formData = new FormData();
      const map = { "0": ["variables.image"] };
      formData.append("operations", JSON.stringify(operations));
      formData.append("map", JSON.stringify(map));
      if (imageFile) formData.append("0", imageFile);

      const response = await fetch(env.graphqlUrl, {
        method: "POST",
        body: formData,
        headers: { 
          
          "x-apollo-operation-name": "CreateTraining" ,
           "apollo-require-preflight": "true",
          "Authorization": `Bearer ${getCookie('token')}`
        
        },
      });
      formData.forEach((value, key) => {
        if (key === 'operations' || key === 'map') {
          console.log(`${key}:`, JSON.parse(value as string));
        } else {
          console.log(`${key}:`, value);
        }
      });

      const result = await response.json();
      if (result.errors) throw result.errors;

      console.log("Training created:", result.data);
      
      // Add success toast notification
      toast({
        title: "Training Created Successfully",
        description: `"${newTraining.title}" has been added to your trainings.`,
        variant: "default",
      });
      
      onClose();
      resetForm();
    } catch (err) {
      console.error("Error creating training:", err);
      setError(
        err instanceof Error ? err.message : "Failed to create training. Please try again."
      );
      
      // Add error toast notification
      toast({
        title: "Error Creating Training",
        description: err instanceof Error ? err.message : "Failed to create training. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`bg-white max-w-4xl w-[95%] ${isMobile ? "h-[100vh]" : "h-[90vh]"} flex flex-col`}
      >
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-4 border-b">
            <DialogTitle className="text-2xl font-bold">
              Create New Training - Step {currentStep} of 5
            </DialogTitle>
            <div className="flex items-center gap-4 mt-6">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="flex-1">
                  <div className="relative">
                    <div
                      className={`h-2 rounded-full ${
                        step <= currentStep ? "bg-primary" : "bg-muted"
                      }`}
                    />
                    <div
                      className={`absolute -top-2 left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full border-2 ${
                        step < currentStep
                          ? "bg-primary border-primary"
                          : step === currentStep
                          ? "bg-white border-primary"
                          : "bg-white border-muted"
                      } flex items-center justify-center`}
                    >
                      <span
                        className={`text-sm ${
                          step <= currentStep ? "text-primary" : "text-muted-foreground"
                        }`}
                      >
                        {step}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`text-center mt-4 text-sm ${
                      step <= currentStep ? "text-primary font-medium" : "text-muted-foreground"
                    }`}
                  >
                    {step === 1
                      ? "Basic Info"
                      : step === 2
                      ? "Provider"
                      : step === 3
                      ? "Instructor"
                      : step === 4
                      ? "Schedule"
                      : "Additional"}
                  </div>
                </div>
              ))}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4" style={{ marginBottom: "80px" }}>
            {currentStep === 1 && (
              <BasicInfoStep
                newTraining={newTraining}
                setNewTraining={setNewTraining}
                setImageFile={setImageFile}
                imagePreview={imagePreview}
                setImagePreview={setImagePreview}
              />
            )}
            {currentStep === 2 && (
              <ProviderStep 
                newTraining={newTraining}
                setNewTraining={setNewTraining}
              />
            )}
            {currentStep === 3 && (
              <InstructorStep newTraining={newTraining} setNewTraining={setNewTraining} />
            )}
            {currentStep === 4 && (
              <ScheduleStep newTraining={newTraining} setNewTraining={setNewTraining} />
            )}
            {currentStep === 5 && (
              <AdditionalDetailsStep newTraining={newTraining} setNewTraining={setNewTraining} />
            )}
          </div>

          <div className="fixed bottom-0 left-0 right-0 bg-white z-20 pt-4 pb-4 border-t w-full shadow-md">
            <div className="flex justify-between gap-2 px-6 max-w-4xl mx-auto">
              <Button variant="outline" type="button" onClick={onClose}>
                Cancel
              </Button>
              <div className="flex gap-2">
                {currentStep > 1 && (
                  <Button type="button" variant="outline" onClick={handlePrevious}>
                    Previous
                  </Button>
                )}
                {currentStep < 5 ? (
                  <Button type="button" onClick={handleNext} disabled={!isStepComplete(currentStep)}>
                    Next
                  </Button>
                ) : (
                  <Button type="submit" disabled={loading}>
                    {loading ? "Creating..." : "Create Training"}
                  </Button>
                )}
              </div>
            </div>
            {error && <p className="text-red-500 mt-2 px-6 max-w-4xl mx-auto">{error}</p>}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTrainingModal;